import Unit from '@/domain/model/unit/Unit.ts';
import Percentage from '@/domain/model/unit/Percentage.ts';

export default class Energy extends Unit {
  static ZERO: Energy = new Energy(0);

  // private! Use Energy.fromKwh()
  constructor(kwh: number) {
    super(kwh);
  }

  static ofMwh(mwh: number): Energy {
    return new Energy(1e3 * mwh);
  }

  static ofKwh(kwh: number): Energy {
    return new Energy(kwh);
  }

  static ofWh(wh: number): Energy {
    return new Energy(1e-3 * wh);
  }

  static of(energy: number, unitString: string): Energy {
    switch (unitString.toLowerCase()) {
      case 'wh':
        return Energy.ofWh(energy);
      case 'kwh':
        return Energy.ofKwh(energy);
      case 'mwh':
        return Energy.ofMwh(energy);
      default:
        throw new Error(`unit ${unitString} is not supported`);
    }
  }

  asWh() {
    return this.getValue() * 1e3;
  }

  asKwh() {
    return this.getValue();
  }

  asMwh() {
    return this.getValue() / 1e3;
  }

  asStringOfUnit(unit: string) {
    const asUnit =
      unit === 'Wh'
        ? this.asWh()
        : unit === 'kWh'
          ? this.asKwh()
          : this.asMwh();
    return asUnit >= 1000
      ? Number(asUnit).toFixed(0)
      : Number(asUnit).toPrecision(3);
  }

  format(precision = undefined): number {
    let magnitude = {
      kWh: 1,
      MWh: 1e3,
      GWh: 1e6,
      TWh: 1e9,
    }[this.getFormattedUnit()];
    const formattedValue = Number(this.getValue() / magnitude);
    return precision === undefined
      ? formattedValue
      : Number(formattedValue.toPrecision(precision));
  }

  unit(): 'kWh' | 'TWh' | 'GWh' | 'MWh' {
    return this.getFormattedUnit();
  }

  getFormattedUnit(): 'kWh' | 'TWh' | 'GWh' | 'MWh' {
    const value = this.getValue();
    if (value >= 10000000000) {
      return 'TWh';
    } else if (value >= 10000000) {
      return 'GWh';
    } else if (value >= 10000) {
      return 'MWh';
    } else {
      return 'kWh';
    }
  }

  toString(): string {
    let precision: number;
    if (this.asKwh() < 10.0) precision = 2;
    else if (this.asKwh() < 100.0) precision = 3;
    else precision = 4;
    return `${this.format(precision)} ${this.getFormattedUnit()}`;
  }

  static zero(): Energy {
    return new Energy(0);
  }

  divideBy(otherEnergy: Energy): Percentage {
    return Percentage.ofRatio(this.asWh() / otherEnergy.asWh());
  }

  subtract(otherEnergy: Energy): Energy {
    return this.combine(otherEnergy, (a, b) => a - b);
  }

  add(otherEnergy: Energy): Energy {
    return this.combine(otherEnergy, (a, b) => a + b);
  }

  times(scalar): Energy {
    return Energy.ofWh(this.asWh() * scalar);
  }

  combine(otherEnergy: Energy, lambda): Energy {
    return Energy.ofWh(lambda(this.asWh(), otherEnergy.asWh()));
  }
}
